import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PasswordField = ({ testId, value, onChange, show, onToggle, placeholder }) => (
  <div className="relative">
    <input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={onChange}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white focus:outline-none focus:border-purple-500/50 transition" placeholder={placeholder} />
    <button type="button" data-testid={`${testId}-toggle`} onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition">
      {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  </div>
);

export const ProfileSettingsForm = ({ currentUser, authToken }) => {
  const [name, setName] = useState(currentUser.name || "");
  const [email, setEmail] = useState(currentUser.email || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) { setMsg({ text: "Name and email are required.", type: "error" }); return; }
    setSaving(true); setMsg({ text: "", type: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Update failed");
      setMsg({ text: "Profile updated! Refresh to see changes.", type: "success" });
    } catch (e) { setMsg({ text: e.message, type: "error" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-white/70 text-sm mb-1.5">Display Name</label>
        <input data-testid="settings-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500/50 transition" />
      </div>
      <div>
        <label className="block text-white/70 text-sm mb-1.5">Email Address</label>
        <input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500/50 transition" />
      </div>
      {msg.text && (
        <p data-testid="settings-profile-msg" className={`text-sm ${msg.type === "error" ? "text-red-400" : "text-green-400"}`}>{msg.text}</p>
      )}
      <button data-testid="settings-save-profile" onClick={handleSave} disabled={saving}
        className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl transition text-sm font-medium">
        {saving ? "Saving..." : "Update Profile"}
      </button>
    </div>
  );
};

export const PasswordChangeForm = ({ authToken }) => {
  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });

  const handleChange = async () => {
    if (!current || !newPass) { setMsg({ text: "All fields are required.", type: "error" }); return; }
    if (newPass.length < 6) { setMsg({ text: "New password must be at least 6 characters.", type: "error" }); return; }
    if (newPass !== confirm) { setMsg({ text: "New passwords don't match.", type: "error" }); return; }
    setSaving(true); setMsg({ text: "", type: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ current_password: current, new_password: newPass }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Password change failed");
      setMsg({ text: "Password changed successfully!", type: "success" });
      setCurrent(""); setNewPass(""); setConfirm("");
    } catch (e) { setMsg({ text: e.message, type: "error" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-white/70 text-sm mb-1.5">Current Password</label>
        <PasswordField testId="settings-current-password" value={current} onChange={(e) => setCurrent(e.target.value)}
          show={showCurrent} onToggle={() => setShowCurrent(!showCurrent)} placeholder="Enter current password" />
      </div>
      <div>
        <label className="block text-white/70 text-sm mb-1.5">New Password</label>
        <PasswordField testId="settings-new-password" value={newPass} onChange={(e) => setNewPass(e.target.value)}
          show={showNew} onToggle={() => setShowNew(!showNew)} placeholder="At least 6 characters" />
      </div>
      <div>
        <label className="block text-white/70 text-sm mb-1.5">Confirm New Password</label>
        <PasswordField testId="settings-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} placeholder="Confirm new password" />
      </div>
      {msg.text && (
        <p data-testid="settings-password-msg" className={`text-sm ${msg.type === "error" ? "text-red-400" : "text-green-400"}`}>{msg.text}</p>
      )}
      <button data-testid="settings-change-password" onClick={handleChange} disabled={saving}
        className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl transition text-sm font-medium">
        {saving ? "Changing..." : "Change Password"}
      </button>
    </div>
  );
};
